import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import layerTop from '../../images/brand/layer-top.png';
import layerMid from '../../images/brand/layer-mid.png';
import layerBottom from '../../images/brand/layer-bottom.png';
import './AtomArchitecture.css';

const AtomArchitecture: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showBg, setShowBg] = useState(false);

  // Detectar si es móvil para ajustar la distancia de separación
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile(); // Chequeo inicial
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Mostrar/ocultar fondo solo cuando la sección está en viewport (activación anticipada para evitar "caída")
  useEffect(() => {
    setShowBg(true); // encender inicialmente

    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => setShowBg(entry.isIntersecting));
      },
      {
        threshold: 0,
        rootMargin: '100% 0px 100% 0px' // se activa antes de entrar y se apaga después de salir
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  // --- ANIMACIONES DE LAS CAPAS (RESPONSIVE) ---
  // PC: [-90, -130] | Móvil: [-50, -80] (Proporcional al tamaño de imagen)
  const topValues = isMobile ? [-50, -80] : [-90, -130];
  const bottomValues = isMobile ? [50, 80] : [90, 130];

  const topY = useTransform(smoothProgress, [0, 1], topValues);
  const midY = useTransform(smoothProgress, [0, 1], [0, 0]);
  const bottomY = useTransform(smoothProgress, [0, 1], bottomValues);

  // --- ANIMACIONES DE LOS TEXTOS ---
  const opacityText1 = useTransform(smoothProgress, [0, 0.25], [1, 0]);
  const yText1 = useTransform(smoothProgress, [0, 0.35], [0, -100]);

  const opacityText2 = useTransform(smoothProgress, [0.30, 0.45, 0.55, 0.70], [0, 1, 1, 0]);
  const yText2 = useTransform(smoothProgress, [0.30, 0.70], [100, -100]);

  const opacityText3 = useTransform(smoothProgress, [0.65, 0.85], [0, 1]);
  const yText3 = useTransform(smoothProgress, [0.65, 1], [100, 0]);

  return (
    <section ref={containerRef} className="arch-scroll-container">
      <div className="arch-sticky-wrapper">
        <iframe
          src="/fondo.html"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 'none',
            pointerEvents: 'none',
            zIndex: 0,
            opacity: showBg ? 1 : 0,
            transition: 'opacity 400ms ease'
          }}
          title="Animated Background"
        />

        <div className="arch-content-grid">

          {/* COLUMNA VISUALS */}
          <div className="arch-visuals-col">
            <div className="arch-layers-stack">

              {/* Layer Top */}
              <motion.img
                src={layerTop}
                className="arch-layer layer-top"
                style={{ y: topY, zIndex: 3 }}
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                alt="Top"
              />

              {/* Layer Mid */}
              <motion.img
                src={layerMid}
                className="arch-layer layer-mid"
                style={{ y: midY, zIndex: 2 }}
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                alt="Core"
              />

              {/* Layer Bottom */}
              <motion.img
                src={layerBottom}
                className="arch-layer layer-bottom"
                style={{ y: bottomY, zIndex: 1 }}
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                alt="Base"
              />
            </div>
          </div>

          {/* COLUMNA TEXTOS */}
          <div className="arch-text-col">
            <div className="arch-text-slider">
              <motion.div className="arch-text-block" style={{ opacity: opacityText1, y: yText1 }}>
                <h2 className="arch-title">The DEX Layer 3</h2>
                <p className="arch-desc">Atom is developing a new type of blockchain: a Layer 3 purpose-built for high-frequency financial applications.</p>
              </motion.div>

              <motion.div className="arch-text-block" style={{ opacity: opacityText2, y: yText2 }}>
                <h2 className="arch-title">An Enshrined Exchange</h2>
                <p className="arch-desc">The Atom DEX Alpha is the universal financial engine at the core of our Layer 3. It creates a verifiable CLOB that unifies trading instantly.</p>
              </motion.div>

              <motion.div className="arch-text-block" style={{ opacity: opacityText3, y: yText3 }}>
                <h2 className="arch-title">Optimized for Speed</h2>
                <p className="arch-desc">
                  Every node runs the AtomVM 5.0 to provide infrastructure that is verifiable and low latency by design.{" "}
                  <a
                    href="https://dex.atomfoundation.io/"
                    className="arch-inline-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Click Here
                  </a>
                </p>
              </motion.div>
            </div>

            <div className="arch-cta">
              <Link className="thm-btn agency-btn" to="/dex-alpha">
                <span className="text">View Dex Testnet</span>
                <span className="arrow">
                  <span className="arrow-icon">
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 28 28"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="5.06592"
                        y="19.9785"
                        width="20.5712"
                        height="2.61221"
                        transform="rotate(-40.2798 5.06592 19.9785)"
                        fill="white"
                      />
                      <rect
                        x="7.97095"
                        y="7.24463"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 7.97095 7.24463)"
                        fill="white"
                      />
                      <rect
                        x="11.6523"
                        y="7.54834"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 11.6523 7.54834)"
                        fill="white"
                      />
                      <rect
                        x="15.334"
                        y="7.85205"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 15.334 7.85205)"
                        fill="white"
                      />
                      <rect
                        x="18.7119"
                        y="11.8374"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 18.7119 11.8374)"
                        fill="white"
                      />
                      <rect
                        x="18.4084"
                        y="15.52"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 18.4084 15.52)"
                        fill="white"
                      />
                      <rect
                        x="18.104"
                        y="19.2012"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 18.104 19.2012)"
                        fill="white"
                      />
                    </svg>
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 28 28"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="5.06592"
                        y="19.9785"
                        width="20.5712"
                        height="2.61221"
                        transform="rotate(-40.2798 5.06592 19.9785)"
                        fill="white"
                      />
                      <rect
                        x="7.97095"
                        y="7.24463"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 7.97095 7.24463)"
                        fill="white"
                      />
                      <rect
                        x="11.6523"
                        y="7.54834"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 11.6523 7.54834)"
                        fill="white"
                      />
                      <rect
                        x="15.334"
                        y="7.85205"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 15.334 7.85205)"
                        fill="white"
                      />
                      <rect
                        x="18.7119"
                        y="11.8374"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 18.7119 11.8374)"
                        fill="white"
                      />
                      <rect
                        x="18.4084"
                        y="15.52"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 18.4084 15.52)"
                        fill="white"
                      />
                      <rect
                        x="18.104"
                        y="19.2012"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 18.104 19.2012)"
                        fill="white"
                      />
                    </svg>
                  </span>
                </span>
              </Link>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default AtomArchitecture;